/**
 * Appointment Context - Manages appointment bookings
 */
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Appointment } from '@/types';
import { useAuth } from './AuthContext';

interface AppointmentContextType {
  appointments: Appointment[] | null;
  loading: boolean;
  error: string | null;
  listAppointments: (filters?: AppointmentFilters) => Promise<void>;
  getAppointment: (appointmentId: string) => Promise<Appointment | null>;
  getUserPendingAppointments: (userId: string) => Promise<Appointment[]>;
  subscribeToUserPendingAppointments: (
    userId: string,
    callback: (appointments: Appointment[]) => void
  ) => Unsubscribe;
  createAppointment: (data: CreateAppointmentData) => Promise<string>;
  updateAppointment: (appointmentId: string, data: Partial<Appointment>) => Promise<void>;
  cancelAppointment: (appointmentId: string) => Promise<void>;
  confirmAppointment: (appointmentId: string) => Promise<void>;
  completeAppointment: (appointmentId: string) => Promise<void>;
  getAvailableTimeSlots: (barberId: string, date: Date) => Promise<string[]>;
}

interface AppointmentFilters {
  franchiseId?: string;
  branchId?: string;
  barberId?: string;
  userId?: string;
  status?: string;
  dateFrom?: Timestamp;
  dateTo?: Timestamp;
}

interface CreateAppointmentData {
  franchiseId: string;
  branchId: string;
  barberId: string;
  barberName: string;
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  serviceDuration: number;
  dateTime: Timestamp;
  notes?: string;
}

const AppointmentContext = createContext<AppointmentContextType | undefined>(undefined);

export function AppointmentProvider({ children }: { children: ReactNode }) {
  const { user, firebaseUser } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listAppointments = useCallback(async (filters: AppointmentFilters = {}) => {
    setLoading(true);
    setError(null);
    try {
      let q = query(collection(db, 'appointments'), orderBy('dateTime', 'desc'));

      if (filters.franchiseId) {
        q = query(q, where('franchiseId', '==', filters.franchiseId));
      }
      if (filters.branchId) {
        q = query(q, where('branchId', '==', filters.branchId));
      }
      if (filters.barberId) {
        q = query(q, where('barberId', '==', filters.barberId));
      }
      if (filters.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      const snapshot = await getDocs(q);
      let appointmentsList = snapshot.docs.map(doc => ({
        appointmentId: doc.id,
        ...doc.data(),
      })) as Appointment[];

      // Client-side filtering for date range (can't use multiple range queries)
      if (filters.dateFrom) {
        appointmentsList = appointmentsList.filter(
          apt => apt.dateTime.toMillis() >= filters.dateFrom!.toMillis()
        );
      }
      if (filters.dateTo) {
        appointmentsList = appointmentsList.filter(
          apt => apt.dateTime.toMillis() <= filters.dateTo!.toMillis()
        );
      }

      setAppointments(appointmentsList);
    } catch (err: any) {
      console.error('Error listing appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, []);

  const getAppointment = useCallback(async (appointmentId: string): Promise<Appointment | null> => {
    try {
      const docRef = doc(db, 'appointments', appointmentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          appointmentId: docSnap.id,
          ...docSnap.data(),
        } as Appointment;
      }
      return null;
    } catch (err: any) {
      console.error('Error getting appointment:', err);
      throw new Error(err.message || 'Failed to get appointment');
    }
  }, []);

  /**
   * Get all pending/confirmed appointments for a user that haven't been converted to tickets yet
   */
  const getUserPendingAppointments = useCallback(async (userId: string): Promise<Appointment[]> => {
    try {
      const now = new Date();
      const q = query(
        collection(db, 'appointments'),
        where('userId', '==', userId),
        where('status', 'in', ['pending', 'confirmed']),
        orderBy('dateTime', 'asc')
      );

      const snapshot = await getDocs(q);
      const allAppointments = snapshot.docs.map(doc => ({
        appointmentId: doc.id,
        ...doc.data(),
      })) as Appointment[];

      // Filter out past appointments and those already converted to tickets
      return allAppointments.filter(apt => {
        const aptDate = apt.dateTime.toDate();
        const isNotPast = aptDate >= now;
        const notConverted = !apt.queueId; // Not yet converted to ticket
        return isNotPast && notConverted;
      });
    } catch (err: any) {
      console.error('Error getting user pending appointments:', err);
      return [];
    }
  }, []);

  /**
   * Subscribe to user's pending appointments in real-time
   */
  const subscribeToUserPendingAppointments = useCallback((
    userId: string,
    callback: (appointments: Appointment[]) => void
  ): Unsubscribe => {
    const q = query(
      collection(db, 'appointments'),
      where('userId', '==', userId),
      where('status', 'in', ['pending', 'confirmed']),
      orderBy('dateTime', 'asc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const now = new Date();
        const allAppointments = snapshot.docs.map(doc => ({
          appointmentId: doc.id,
          ...doc.data(),
        })) as Appointment[];

        // Filter out past appointments and those already converted to tickets
        const filteredAppointments = allAppointments.filter(apt => {
          const aptDate = apt.dateTime.toDate();
          const isNotPast = aptDate >= now;
          const notConverted = !apt.queueId;
          return isNotPast && notConverted;
        });

        callback(filteredAppointments);
      },
      (error) => {
        console.error('Error subscribing to user pending appointments:', error);
        callback([]);
      }
    );
  }, []);

  const createAppointment = useCallback(async (data: CreateAppointmentData): Promise<string> => {
    if (!firebaseUser || !user) throw new Error('User not authenticated');

    try {
      // Validate that the appointment is not in the past
      const appointmentDate = data.dateTime.toDate();
      const now = new Date();

      if (appointmentDate < now) {
        throw new Error('No puedes agendar una cita en el pasado');
      }

      const docRef = await addDoc(collection(db, 'appointments'), {
        ...data,
        userId: firebaseUser.uid,
        userName: user.displayName || 'Usuario',
        userEmail: user.email,
        userPhone: user.phoneNumber,
        status: 'pending',
        reminderSent: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (err: any) {
      console.error('Error creating appointment:', err);
      throw new Error(err.message || 'Failed to create appointment');
    }
  }, [firebaseUser, user]);

  const updateAppointment = useCallback(async (appointmentId: string, data: Partial<Appointment>) => {
    try {
      const docRef = doc(db, 'appointments', appointmentId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });

      await listAppointments();
    } catch (err: any) {
      console.error('Error updating appointment:', err);
      throw new Error(err.message || 'Failed to update appointment');
    }
  }, [listAppointments]);

  const cancelAppointment = useCallback(async (appointmentId: string) => {
    try {
      await updateAppointment(appointmentId, { status: 'cancelled' });
    } catch (err: any) {
      console.error('Error cancelling appointment:', err);
      throw new Error(err.message || 'Failed to cancel appointment');
    }
  }, [updateAppointment]);

  const confirmAppointment = useCallback(async (appointmentId: string) => {
    try {
      await updateAppointment(appointmentId, { status: 'confirmed' });
    } catch (err: any) {
      console.error('Error confirming appointment:', err);
      throw new Error(err.message || 'Failed to confirm appointment');
    }
  }, [updateAppointment]);

  const completeAppointment = useCallback(async (appointmentId: string) => {
    try {
      await updateAppointment(appointmentId, { status: 'completed' });
    } catch (err: any) {
      console.error('Error completing appointment:', err);
      throw new Error(err.message || 'Failed to complete appointment');
    }
  }, [updateAppointment]);

  const getAvailableTimeSlots = useCallback(async (barberId: string, date: Date): Promise<string[]> => {
    try {
      // Get appointments for this barber on this date
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('barberId', '==', barberId),
        where('dateTime', '>=', Timestamp.fromDate(startOfDay)),
        where('dateTime', '<=', Timestamp.fromDate(endOfDay))
      );

      const appointmentsSnapshot = await getDocs(appointmentsQuery);

      // Filter by status on the client side to avoid Firestore query limitations
      const bookedSlots = appointmentsSnapshot.docs
        .filter(doc => {
          const status = doc.data().status;
          return status === 'pending' || status === 'confirmed';
        })
        .map(doc => {
          const data = doc.data();
          const appointmentDate = data.dateTime.toDate();
          const serviceDuration = data.serviceDuration || 30; // Default 30 minutes

          // Block the appointment slot and the duration after it
          const slots = [];
          slots.push(appointmentDate.toTimeString().substring(0, 5)); // HH:MM format

          // Block additional slots based on service duration
          const slotsToBlock = Math.ceil(serviceDuration / 30);
          for (let i = 1; i < slotsToBlock; i++) {
            const nextSlot = new Date(appointmentDate);
            nextSlot.setMinutes(nextSlot.getMinutes() + (i * 30));
            slots.push(nextSlot.toTimeString().substring(0, 5));
          }

          return slots;
        })
        .flat();

      // Check active queue tickets for this barber (only if date is today)
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      let queueBlockedSlots: string[] = [];

      if (isToday) {
        // Get active queue tickets for this barber
        const queueQuery = query(
          collection(db, 'queues'),
          where('barberId', '==', barberId),
          where('status', 'in', ['waiting', 'ready', 'in_service'])
        );

        const queueSnapshot = await getDocs(queueQuery);

        if (!queueSnapshot.empty) {
          // Calculate total time needed for queue
          let totalQueueTime = 0;

          queueSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const status = data.status;

            // For in_service tickets, calculate remaining time
            if (status === 'in_service' && data.serviceStartedAt && data.serviceDuration) {
              const startedAt = data.serviceStartedAt.toDate();
              const elapsedMinutes = Math.floor((now.getTime() - startedAt.getTime()) / (1000 * 60));
              const remainingMinutes = Math.max(0, data.serviceDuration - elapsedMinutes);
              totalQueueTime += remainingMinutes;
            } else {
              // For waiting/ready tickets, estimate service duration
              const estimatedDuration = data.serviceDuration || data.estimatedWaitTime || 30;
              totalQueueTime += estimatedDuration;
            }
          });

          // Block slots from now until queue is done
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const queueEndMinutes = currentMinutes + totalQueueTime;

          // Generate blocked slots
          for (let min = currentMinutes; min < queueEndMinutes; min += 30) {
            const hour = Math.floor(min / 60);
            const minute = min % 60;

            // Only block slots within business hours (9 AM to 8 PM)
            if (hour >= 9 && hour < 20) {
              const slotTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
              queueBlockedSlots.push(slotTime);
            }
          }
        }
      }

      // Combine all blocked slots
      const allBlockedSlots = [...bookedSlots, ...queueBlockedSlots];

      // Generate all possible time slots (9 AM to 8 PM, 30-minute intervals)
      const allSlots: string[] = [];
      for (let hour = 9; hour < 20; hour++) {
        allSlots.push(`${hour.toString().padStart(2, '0')}:00`);
        allSlots.push(`${hour.toString().padStart(2, '0')}:30`);
      }

      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Filter out booked slots and past slots (if today)
      return allSlots.filter(slot => {
        // Check if slot is already booked or blocked by queue
        if (allBlockedSlots.includes(slot)) {
          return false;
        }

        // If the selected date is today, filter out past times
        if (isToday) {
          const [slotHour, slotMinute] = slot.split(':').map(Number);

          // If slot hour is before current hour, exclude it
          if (slotHour < currentHour) {
            return false;
          }

          // If slot hour equals current hour, check minutes
          if (slotHour === currentHour && slotMinute <= currentMinute) {
            return false;
          }
        }

        return true;
      });
    } catch (err: any) {
      console.error('Error getting available time slots:', err);
      throw new Error(err.message || 'Failed to get available time slots');
    }
  }, []);

  const value: AppointmentContextType = {
    appointments,
    loading,
    error,
    listAppointments,
    getAppointment,
    getUserPendingAppointments,
    subscribeToUserPendingAppointments,
    createAppointment,
    updateAppointment,
    cancelAppointment,
    confirmAppointment,
    completeAppointment,
    getAvailableTimeSlots,
  };

  return <AppointmentContext.Provider value={value}>{children}</AppointmentContext.Provider>;
}

export function useAppointment() {
  const context = useContext(AppointmentContext);
  if (context === undefined) {
    throw new Error('useAppointment must be used within an AppointmentProvider');
  }
  return context;
}
